import { SingleDimension, UnitType } from "../../types/FoundItemTypes";

export const convertToApiFormat = (data: any, unit: number) => {
  const conversionFactor = unit === 1 ? {
    height: { primaryUnit: 'Ft', secondaryUnit: 'In' },
    width: { primaryUnit: 'Ft', secondaryUnit: 'In' },
    length: { primaryUnit: 'Ft', secondaryUnit: 'In' },
    weight: { primaryUnit: 'Lb', secondaryUnit: 'Oz' },
  } : {
    height: { primaryUnit: 'M', secondaryUnit: 'Cm' },
    width: { primaryUnit: 'M', secondaryUnit: 'Cm' },
    length: { primaryUnit: 'M', secondaryUnit: 'Cm' },
    weight: { primaryUnit: 'Kg', secondaryUnit: 'Gm' },
  };

  return {
    heightPrimaryValue: data.height.main,
    heightPrimaryUnit: conversionFactor.height.primaryUnit,
    heightSecondaryValue: data.height.sub,
    heightSecondaryUnit: conversionFactor.height.secondaryUnit,
    widthPrimaryValue: data.width.main,
    widthPrimaryUnit: conversionFactor.width.primaryUnit,
    widthSecondaryValue: data.width.sub,
    widthSecondaryUnit: conversionFactor.width.secondaryUnit,
    lengthPrimaryValue: data.length.main,
    lengthPrimaryUnit: conversionFactor.length.primaryUnit,
    lengthSecondaryValue: data.length.sub,
    lengthSecondaryUnit: conversionFactor.length.secondaryUnit,
    weightPrimaryValue: data.weight.main,
    weightPrimaryUnit: conversionFactor.weight.primaryUnit,
    weightSecondaryValue: data.weight.sub,
    weightSecondaryUnit: conversionFactor.weight.secondaryUnit,
    measurementType: unit,
  };
};


export const convertMeasurementToApiFormat = (data: any, unit: number) => {
  const conversionFactor = unit === 1 ? {
    height: 'In',
    width: 'In',
    length: 'In',
    weight: 'Lb',
  } : {
    height: 'Cm',
    width: 'Cm',
    length: 'Cm',
    weight: 'Kg',
  };

  return {
    shippingBoxHeight: data?.height,
    shippingBoxHeightUnit: conversionFactor.height,
    shippingBoxWidth: data?.width,
    shippingBoxWidthUnit: conversionFactor.width,
    shippingBoxLength: data?.length,
    shippingBoxLengthUnit: conversionFactor.length,
    shippingBoxWeight: data?.weight,
    shippingBoxWeightUnit: conversionFactor.weight,
    measurementType: unit,
  };
};

type UnitConversion = {
  primaryUnit: string;
  secondaryUnit: string;
};

const getUnitConversionFactors = (unit: number): Record<string, UnitConversion> => {
  return unit === 1
    ? {
      height: { primaryUnit: "", secondaryUnit: "In" },
      width: { primaryUnit: "", secondaryUnit: "In" },
      length: { primaryUnit: "", secondaryUnit: "In" },
      weight: { primaryUnit: "Lb", secondaryUnit: "" },
    }
    : {
      height: { primaryUnit: "", secondaryUnit: "Cm" },
      width: { primaryUnit: "", secondaryUnit: "Cm" },
      length: { primaryUnit: "", secondaryUnit: "Cm" },
      weight: { primaryUnit: "Kg", secondaryUnit: "" },
    };
};

const formatMeasurementWithUnits = (measurement: { main: number; sub: number }, conversionFactor: UnitConversion) => {
  const mainValue = (Number(measurement.main) ?? 0).toFixed(2);
  const subValue = (Number(measurement.sub) ?? 0).toFixed(2);

  return {
    main: conversionFactor.primaryUnit ? `${mainValue}  ${conversionFactor.primaryUnit}` : "",
    sub: conversionFactor.secondaryUnit ? `${subValue} ${conversionFactor.secondaryUnit}` : "",
  };
};

export const applyUnitConversions = (data: any, unit: number) => {
  if (!data) return data;
  const conversionFactor = getUnitConversionFactors(unit);

  const updatedData = {
    Length: formatMeasurementWithUnits(data.length, conversionFactor.length),
    Width: formatMeasurementWithUnits(data.width, conversionFactor.width),
    Height: formatMeasurementWithUnits(data.height, conversionFactor.height),
    Weight: formatMeasurementWithUnits(data.weight, conversionFactor.weight),
  };

  return {
    ...updatedData,
  };
};


export function convertSingleDimensionsUnits(
  values: SingleDimension,
  oldUnit: UnitType,
  newUnit: UnitType
): SingleDimension {
  // Skip conversion if units are the same
  if (oldUnit === newUnit) {
    return { ...values };
  }

  const convert = (type: keyof SingleDimension): number => {
    const value = values[type] ?? 0;

    if (type === "weight") {
      if (oldUnit === 1 && newUnit === 2) {
        // Imperial to Metric: lb → kg
        const totalKg = value * 0.45359237;
        // Custom rounding: if decimal part is .5 or higher, round up to next whole number
        const decimalPart = totalKg - Math.floor(totalKg);
        return decimalPart >= 0.9 ? Math.ceil(totalKg) : parseFloat(totalKg.toFixed(2));
      } else if (oldUnit === 2 && newUnit === 1) {
        // Metric to Imperial: kg → lb
        const totalLb = value * 2.20462;
        // Custom rounding: if decimal part is .5 or higher, round up to next whole number
        const decimalPart = totalLb - Math.floor(totalLb);
        return decimalPart >= 0.9 ? Math.ceil(totalLb) : parseFloat(totalLb.toFixed(2));
      }
    } else {
      // For dimensions (length, width, height)
      if (oldUnit === 1 && newUnit === 2) {
        // Imperial to Metric: inches → cm
        const cmValue = value * 2.54;
        const decimalPart = cmValue - Math.floor(cmValue);
        return decimalPart >= 0.9 ? Math.ceil(cmValue) : parseFloat(cmValue.toFixed(2));
      } else if (oldUnit === 2 && newUnit === 1) {
        // Metric to Imperial: cm → inches
        const inchValue = value * 0.393701;
        const decimalPart = inchValue - Math.floor(inchValue);
        return decimalPart >= 0.9 ? Math.ceil(inchValue) : parseFloat(inchValue.toFixed(2));
      }
    }

    // Fallback (no conversion needed)
    return value;
  };

  return {
    length: convert("length"),
    width: convert("width"),
    height: convert("height"),
    weight: convert("weight"),
  };
}